// Direct API access to avoid library complications
const USER_AGENT = 'diskcatalog-app/1.0.0 (https://github.com/user/diskcatalog-app)';

export interface MusicBrainzArtist {
  id: string;
  name: string;
  'sort-name'?: string;
  type?: 'Person' | 'Group' | 'Orchestra' | 'Choir' | 'Character' | 'Other';
  'type-id'?: string;
  score: number;
  disambiguation?: string;
  country?: string; // ISO code
  area?: {
    id: string;
    name: string;
    'sort-name': string;
    iso?: string[];
  };
  'begin-area'?: {
    id: string;
    name: string;
    'sort-name': string;
    iso?: string[];
  };
}

export interface ArtistLookupResult {
  found: boolean;
  isIndividualPerson: boolean;
  artistName: string;
  sortName?: string; // MusicBrainz provided sort name
  disambiguation?: string;
  confidence: number;
  country?: string; // ISO country code (e.g., "US", "GB")
  countryName?: string; // Full country name
  language?: string; // Primary language code
  languageName?: string; // Full language name
}

class MusicBrainzService {
  private cache = new Map<string, ArtistLookupResult>();
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private lastRequestTime = 0;
  private readonly RATE_LIMIT_DELAY = 1100; // 1.1 seconds to be safe

  private async enforceRateLimit(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.RATE_LIMIT_DELAY) {
      const delay = this.RATE_LIMIT_DELAY - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    this.lastRequestTime = Date.now();
  }

  private getCacheKey(artistName: string): string {
    return artistName.toLowerCase().trim();
  }

  private isValidCacheEntry(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  private normalizeArtistName(name: string): string {
    // Remove common Discogs patterns
    return name
      .replace(/\s*\(\d+\)$/, '') // Remove (2) disambiguation numbers
      .replace(/\s*\[.*?\]$/, '') // Remove [Various] or [Compilation] tags
      .trim();
  }

  private calculateConfidence(artist: MusicBrainzArtist, searchName: string): number {
    const normalizedSearch = this.normalizeArtistName(searchName).toLowerCase();
    const normalizedResult = artist.name.toLowerCase();
    
    // Exact match
    if (normalizedSearch === normalizedResult) {
      return 1.0;
    }
    
    // Close match using score from MusicBrainz
    const scoreConfidence = Math.min(artist.score / 100, 1.0);
    
    // Additional confidence based on string similarity
    const similarity = this.calculateStringSimilarity(normalizedSearch, normalizedResult);
    
    return Math.max(scoreConfidence, similarity);
  }

  private calculateStringSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  async lookupArtistType(artistName: string): Promise<ArtistLookupResult> {
    const cacheKey = this.getCacheKey(artistName);
    const cached = this.cache.get(cacheKey);
    
    if (cached && this.isValidCacheEntry(Date.now())) {
      return cached;
    }

    try {
      await this.enforceRateLimit();
      
      const normalizedName = this.normalizeArtistName(artistName);
      
      // Search for the artist using direct API call, including area information
      const searchUrl = `https://musicbrainz.org/ws/2/artist?query=${encodeURIComponent(normalizedName)}&fmt=json&limit=5&inc=area-rels+begin-area-rels`;
      
      const response = await fetch(searchUrl, {
        headers: {
          'User-Agent': USER_AGENT,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`MusicBrainz API error: ${response.status} ${response.statusText}`);
      }
      
      const searchResults = await response.json();
      
      if (!searchResults.artists || searchResults.artists.length === 0) {
        const result: ArtistLookupResult = {
          found: false,
          isIndividualPerson: false, // Default to band/group when not found
          artistName: normalizedName,
          confidence: 0
        };
        
        this.cache.set(cacheKey, result);
        return result;
      }

      // Find the best match
      let bestMatch: MusicBrainzArtist | null = null;
      let bestConfidence = 0;

      for (const artist of searchResults.artists) {
        const confidence = this.calculateConfidence(artist, normalizedName);
        
        if (confidence > bestConfidence && confidence > 0.7) { // Only accept high-confidence matches
          bestMatch = artist;
          bestConfidence = confidence;
        }
      }

      if (!bestMatch) {
        const result: ArtistLookupResult = {
          found: false,
          isIndividualPerson: false,
          artistName: normalizedName,
          confidence: 0
        };
        
        this.cache.set(cacheKey, result);
        return result;
      }

      // Determine if it's an individual person
      const isIndividualPerson = bestMatch.type === 'Person';
      
      // Extract country information
      let country: string | undefined;
      let countryName: string | undefined;
      
      // Try area first (current location), then begin-area (origin)
      const area = bestMatch.area || bestMatch['begin-area'];
      if (area?.iso && area.iso.length > 0) {
        country = area.iso[0]; // Use first ISO code
        countryName = area.name;
      }
      
      const result: ArtistLookupResult = {
        found: true,
        isIndividualPerson,
        artistName: bestMatch.name,
        sortName: bestMatch['sort-name'],
        disambiguation: bestMatch.disambiguation,
        confidence: bestConfidence,
        country,
        countryName
      };

      this.cache.set(cacheKey, result);
      const sortInfo = bestMatch['sort-name'] ? ` sort: "${bestMatch['sort-name']}"` : '';
      console.log(`MusicBrainz lookup: "${artistName}" -> ${bestMatch.name} (${bestMatch.type}, confidence: ${bestConfidence.toFixed(2)})${sortInfo}`);
      
      return result;

    } catch (error) {
      console.warn(`MusicBrainz lookup failed for "${artistName}":`, error);
      
      // Return fallback result
      const result: ArtistLookupResult = {
        found: false,
        isIndividualPerson: false, // Default to band/group on error
        artistName,
        confidence: 0
      };
      
      this.cache.set(cacheKey, result);
      return result;
    }
  }

  // Batch lookup with automatic spacing to respect rate limits
  async batchLookupArtistTypes(
    artistNames: string[], 
    onProgress?: (processed: number, total: number, currentArtist: string) => void
  ): Promise<Map<string, ArtistLookupResult>> {
    const results = new Map<string, ArtistLookupResult>();
    
    console.log(`Starting MusicBrainz batch lookup for ${artistNames.length} artists...`);
    
    for (let i = 0; i < artistNames.length; i++) {
      const artistName = artistNames[i];
      
      // Report progress before processing each artist
      console.log(`Processing artist ${i + 1}/${artistNames.length}: ${artistName}`);
      onProgress?.(i, artistNames.length, artistName);
      
      try {
        const result = await this.lookupArtistType(artistName);
        results.set(artistName, result);
        
        // Report progress after processing each artist
        onProgress?.(i + 1, artistNames.length, artistName);
        
        // Log progress every 10 items
        if ((i + 1) % 10 === 0) {
          console.log(`MusicBrainz progress: ${i + 1}/${artistNames.length} artists processed`);
        }
        
      } catch (error) {
        console.error(`Failed to lookup artist "${artistName}":`, error);
        results.set(artistName, {
          found: false,
          isIndividualPerson: false,
          artistName,
          confidence: 0
        });
      }
    }
    
    // Report final progress
    onProgress?.(artistNames.length, artistNames.length, '');
    
    console.log(`MusicBrainz batch lookup completed. ${results.size} results.`);
    return results;
  }

  // Clear cache (useful for testing)
  clearCache(): void {
    this.cache.clear();
  }

  // Get cache stats
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

export const musicbrainzService = new MusicBrainzService();